// The agent's operation settings — ONE form, two frames. The flow editor
// shows it in a modal; /asistente/:botId/ajustes shows it as a page, because
// "cambia el horario del agente" must be a linkable destination, not a modal
// three clicks deep inside an editor the person may never open.
//
// The form is controlled from outside (value/onChange) so each frame owns its
// own load/save/busy lifecycle against `FlowGateway['settings']`.

import { DEFAULT_KB_DEFERRAL } from '@vysto/shared/bot-config/deferral'

import {
  FormField,
  Select,
  Slider,
  Textarea,
  ToggleSwitch,
  WeeklyScheduleEditor,
} from '../../design-system/index.js'
import type { AgentSettings } from '../admin/flows-data.js'
import { TIMEZONE_OPTIONS } from '../admin/flows-data.js'

export function AgentSettingsForm({
  value,
  onChange,
}: {
  value: AgentSettings
  onChange: (next: AgentSettings) => void
}): JSX.Element {
  return (
    <div className="space-y-5">
      <p className="text-[12px] leading-relaxed text-text-2">
        La identidad y los datos de tu negocio ahora viven en <strong>Perfil del negocio</strong>.
        Aquí quedan los ajustes de operación.
      </p>
      <FormField
        label="Cuando no encuentra información"
        htmlFor="agent-no-match"
        helper="Lo que responde si no hay nada en la base de conocimiento. En vez de inventar, ofrece pasar con una persona. Vacío = mensaje por defecto."
      >
        {/* The placeholder IS the default the runtime sends when this is left
            empty — the same constant, so the helper text above never lies. */}
        <Textarea
          id="agent-no-match"
          value={value.noMatchMessage}
          onChange={(e) => onChange({ ...value, noMatchMessage: e.target.value })}
          placeholder={DEFAULT_KB_DEFERRAL}
        />
      </FormField>
      <div className="space-y-3 border-t border-line pt-4">
        <p className="text-[12px] font-semibold text-text">Estilo de los mensajes</p>
        <p className="text-[11.5px] leading-relaxed text-text-3">
          En WhatsApp lo más natural suele ser texto plano. El agente nunca usa Markdown (**, #);
          estas opciones controlan si usa el formato de WhatsApp y emojis.
        </p>
        <ToggleSwitch
          label="Usar formato (negrita, cursiva)"
          description="Resalta con *negrita* y _cursiva_ de WhatsApp. Apagado: texto plano."
          checked={value.useFormatting}
          onChange={(useFormatting) => onChange({ ...value, useFormatting })}
        />
        <ToggleSwitch
          label="Usar emojis"
          description="Permite algún emoji ocasional. Apagado: sin emojis."
          checked={value.useEmojis}
          onChange={(useEmojis) => onChange({ ...value, useEmojis })}
        />
      </div>

      <div className="space-y-3 border-t border-line pt-4">
        <p className="text-[12px] font-semibold text-text">Agendado de citas</p>
        <ToggleSwitch
          label="Requiere confirmación de un coordinador"
          description="Encendido: el agente aparta el horario y un coordinador confirma la cita desde Agenda (el paciente recibe aviso). Apagado: el agente agenda y confirma la cita solo."
          checked={value.requiresConfirmation}
          onChange={(requiresConfirmation) => onChange({ ...value, requiresConfirmation })}
        />
      </div>

      <div className="space-y-3 border-t border-line pt-4">
        <p className="text-[12px] font-semibold text-text">Horario de atención</p>
        <p className="text-[11.5px] leading-relaxed text-text-3">
          Marca los días y las horas en que atienden. Cuando llega un mensaje fuera de este horario,
          el agente responde con el aviso de abajo. Deja un día cerrado si no atienden.
        </p>
        {/* The same horarios.business_hours the operator console edits. One
            stored value, two surfaces: say so, or each side looks like the only
            one and a change from the other reads as data loss. */}
        <p className="text-[11.5px] leading-relaxed text-text-3">
          Este horario es el mismo que ve tu equipo de INHERTA en la consola: se guarda en un solo
          lugar, así que si lo cambian allá, aquí lo verás actualizado.
        </p>
        <FormField label="Zona horaria" htmlFor="agent-tz">
          <Select
            id="agent-tz"
            options={TIMEZONE_OPTIONS}
            value={value.timezone}
            onChange={(e) => onChange({ ...value, timezone: e.target.value })}
          />
        </FormField>
        <WeeklyScheduleEditor
          week={value.businessHours}
          onChange={(businessHours) => onChange({ ...value, businessHours })}
        />
        <FormField
          label="Mensaje fuera de horario"
          htmlFor="agent-off-hours"
          helper="Se envía cuando alguien escribe fuera del horario de atención. Vacío = sin aviso automático."
        >
          <Textarea
            id="agent-off-hours"
            value={value.outOfHoursMessage}
            onChange={(e) => onChange({ ...value, outOfHoursMessage: e.target.value })}
            placeholder="Gracias por escribirnos. Nuestro horario es de lunes a viernes de 9 a 18 h; te responderemos en cuanto abramos."
          />
        </FormField>
      </div>

      <div className="space-y-4 border-t border-line pt-4">
        <p className="text-[12px] leading-relaxed text-text-2">
          La red de seguridad: cuando el agente falla al responder, pasa la conversación a tu
          equipo. Cuenta los intentos fallidos de toda la conversación, sin importar el flujo. Una
          respuesta correcta reinicia la cuenta.
        </p>
        <Slider
          label={`Pasar con una persona tras ${value.failedAttempts} ${value.failedAttempts === 1 ? 'intento fallido' : 'intentos fallidos'}`}
          value={value.failedAttempts}
          min={1}
          max={10}
          step={1}
          onChange={(failedAttempts) => onChange({ ...value, failedAttempts })}
        />
        <p className="text-[11.5px] leading-relaxed text-text-3">
          Un intento fallido es cuando el agente no pudo entregar una respuesta: el servicio de IA
          falló, o la respuesta generada no pasó el filtro de seguridad. No cuenta como fallido que
          el agente responda con honestidad que no tiene esa información.
        </p>
      </div>
    </div>
  )
}
